import { Fulfillment } from 'api/models/Fulfillment';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelStatus as ModelStatusEnum , ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';

const CLIENT_DOCS_OR_APPROVAL =
  (label: string) =>
    label === ValuationStatus.CLIENT_DOCS_RECEIVED ||
    label === ValuationStatus.CLIENT_APPROVAL_RECEIVED;

export type FulfillmentSideEffectsParams = {
  statusLabel: string;
  existingFulfillment: Fulfillment | null;
  companyMeasurementDateId: number | undefined;
  appCompanyId: string | undefined;
  /** Supporting due date (e.g. addBusinessDays(new Date(), 4)) - caller provides for consistency. Date or ISO string. */
  supportingDueDate: Date | string;
};

export type FulfillmentSideEffectsResult = {
  action: 'create' | 'update' | 'none';
  createPayload?: Partial<Fulfillment> & {
    /** Set when using legacy fulfillment create; omitted when only merging into ensure payload. */
    company_measurement_date?: number;
    model_status: number;
    supporting_due_date: string;
    reviewers: number[];
    preparers: number[];
    is_slc_model?: boolean;
  };
  updatePayload?: Partial<Fulfillment>;
};

/**
 * Returns what fulfillment side effects to merge into the ensure-fulfillment-for-attio-valuation request
 * (see `ensureFulfillmentBeforeAttioValuationStatusPatch`).
 */
export function getFulfillmentSideEffectsForStatusChange(
  params: FulfillmentSideEffectsParams
): FulfillmentSideEffectsResult {
  const {
    statusLabel,
    existingFulfillment,
    companyMeasurementDateId,
    appCompanyId,
    supportingDueDate
  } = params;

  const isClientDocsOrApproval = CLIENT_DOCS_OR_APPROVAL(statusLabel);

  const supportingDueDateStr =
    supportingDueDate instanceof Date ? supportingDueDate.toISOString() : supportingDueDate;

  const cmdIdField =
    companyMeasurementDateId == null ? {} : { company_measurement_date: companyMeasurementDateId };

  if (!existingFulfillment) {
    if (isClientDocsOrApproval) {
      if (statusLabel === ValuationStatus.CLIENT_DOCS_RECEIVED) {
        return {
          action: 'create',
          createPayload: {
            ...cmdIdField,
            model_status: modelStatusMap[ModelStatusEnum.DRAFT],
            supporting_due_date: supportingDueDateStr,
            reviewers: [],
            preparers: [],
            is_slc_model: false,
            is_waiting: false,
          },
        };
      }

      if (statusLabel === ValuationStatus.CLIENT_APPROVAL_RECEIVED) {
        return {
          action: 'create',
          createPayload: {
            ...cmdIdField,
            model_status: modelStatusMap[ModelStatusEnum.FINAL],
            supporting_due_date: supportingDueDateStr,
            reviewers: [],
            preparers: [],
            is_slc_model: false,
            is_waiting: false,
          },
        };
      }
    }

    return {
      action: 'create',
      createPayload: {
        ...cmdIdField,
        is_slc_model: true,
        model_status: modelStatusMap[ModelStatusEnum.DRAFT],
        supporting_due_date: supportingDueDateStr,
        reviewers: [],
        preparers: [],
        is_waiting: false,
      },
    };
  }

  if (!existingFulfillment || !isClientDocsOrApproval || !appCompanyId) {
    return { action: 'none' };
  }

  const isClientDocsReceived = statusLabel === ValuationStatus.CLIENT_DOCS_RECEIVED;

  return {
    action: 'update',
    updatePayload: {
      model_status: modelStatusMap[isClientDocsReceived ? ModelStatusEnum.DRAFT : ModelStatusEnum.FINAL],
      supporting_due_date: supportingDueDateStr,
      is_slc_model: false,
      is_waiting: false,
    },
  };
}
